import { useState, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rfiRapportiniApi, rfiContattiApi, rfiApi } from '../services/api'
import toast from 'react-hot-toast'
import { Download, Send, Paperclip, Trash2, Upload } from 'lucide-react'

interface RapportinoRiga {
  id: number
  odl_id: number
  oggetto: string
  mittente_email?: string
  stato: string
  tecnici_nomi: string[]
  data_apertura?: string
  pdf_url?: string
  guerrato_id?: number
}

interface Contatto {
  id: number
  nome: string
  email?: string
  telefono_whatsapp?: string
  attivo: boolean
}

interface Allegato {
  id: number
  nome_file: string
  dimensione?: number
  created_at?: string
}

function InviaModal({ rapportinoId, onClose }: { rapportinoId: number; onClose: () => void }) {
  const { data: contatti = [] } = useQuery({
    queryKey: ['rfi-contatti-avviso'],
    queryFn: () => rfiContattiApi.list().then(r => (r.data as Contatto[]).filter(c => c.attivo)),
  })
  const [selezionati, setSelezionati] = useState<number[]>([])
  const [canali, setCanali] = useState<string[]>(['email'])

  const inviaMut = useMutation({
    mutationFn: () => rfiRapportiniApi.invia(rapportinoId, { contatto_ids: selezionati, canali }),
    onSuccess: (r) => {
      const { inviate, saltate } = r.data
      toast.success(`Inviato a: ${inviate.join(', ') || 'nessuno'}`)
      if (saltate.length) toast(`Saltati: ${saltate.join(', ')}`, { icon: '⚠️' })
      onClose()
    },
    onError: () => toast.error('Invio fallito'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="card p-4 w-96 space-y-3">
        <h3 className="font-medium">Invia rapportino</h3>
        <div className="space-y-1">
          {contatti.map(c => (
            <label key={c.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={selezionati.includes(c.id)} onChange={e => {
                setSelezionati(e.target.checked ? [...selezionati, c.id] : selezionati.filter(id => id !== c.id))
              }} />
              {c.nome} {c.email ? `· ${c.email}` : ''} {c.telefono_whatsapp ? `· ${c.telefono_whatsapp}` : ''}
            </label>
          ))}
        </div>
        <div className="flex gap-3 text-sm">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={canali.includes('email')} onChange={e => {
              setCanali(e.target.checked ? [...canali, 'email'] : canali.filter(c => c !== 'email'))
            }} /> Email
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={canali.includes('whatsapp')} onChange={e => {
              setCanali(e.target.checked ? [...canali, 'whatsapp'] : canali.filter(c => c !== 'whatsapp'))
            }} /> WhatsApp
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>Annulla</button>
          <button className="btn-primary" disabled={!selezionati.length || !canali.length} onClick={() => inviaMut.mutate()}>
            <Send size={14} className="inline mr-1" /> Invia
          </button>
        </div>
      </div>
    </div>
  )
}

function AllegatiRow({ ordineId }: { ordineId: number }) {
  const qc = useQueryClient()
  const { data: allegati = [] } = useQuery({
    queryKey: ['rfi-allegati', ordineId],
    queryFn: () => rfiApi.listAllegati(ordineId).then(r => r.data as Allegato[]),
  })
  const uploadMut = useMutation({
    mutationFn: (file: File) => rfiApi.uploadAllegato(ordineId, file),
    onSuccess: () => { toast.success('PDF caricato'); qc.invalidateQueries({ queryKey: ['rfi-allegati', ordineId] }) },
    onError: () => toast.error('Upload fallito'),
  })
  const deleteMut = useMutation({
    mutationFn: (allegatoId: number) => rfiApi.deleteAllegato(ordineId, allegatoId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rfi-allegati', ordineId] }),
  })

  return (
    <tr>
      <td colSpan={7} className="bg-slate-50 p-3">
        <div className="space-y-2">
          {allegati.map(a => (
            <div key={a.id} className="flex items-center gap-2 text-sm">
              <button
                type="button"
                className="text-blue-700 underline"
                onClick={async () => {
                  try {
                    const res = await rfiApi.downloadAllegato(ordineId, a.id)
                    const url = URL.createObjectURL(new Blob([res.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.download = a.nome_file
                    document.body.appendChild(link)
                    link.click()
                    link.remove()
                    URL.revokeObjectURL(url)
                  } catch { toast.error('Download fallito') }
                }}
              >
                {a.nome_file}
              </button>
              <button className="btn-ghost" onClick={() => deleteMut.mutate(a.id)}><Trash2 size={12} /></button>
            </div>
          ))}
          <label className="btn-secondary text-xs cursor-pointer inline-flex items-center gap-1">
            <Upload size={12} /> Carica PDF
            <input type="file" accept="application/pdf" className="hidden" onChange={e => {
              const file = e.target.files?.[0]
              if (file) uploadMut.mutate(file)
            }} />
          </label>
        </div>
      </td>
    </tr>
  )
}

export default function RfiRapportini() {
  const { data: righe = [], isLoading } = useQuery({
    queryKey: ['rfi-rapportini-lista'],
    queryFn: () => rfiRapportiniApi.list().then(r => r.data as RapportinoRiga[]),
  })
  const [inviaId, setInviaId] = useState<number | null>(null)
  const [espansoOdl, setEspansoOdl] = useState<number | null>(null)

  if (isLoading) return <div className="p-4">Caricamento...</div>

  return (
    <div className="p-4 space-y-3">
      <h1 className="text-lg font-semibold">Rapportini RFI</h1>
      <table className="w-full text-sm card">
        <thead>
          <tr className="text-left text-xs text-slate-500 uppercase">
            <th className="p-2">Ordine</th>
            <th className="p-2">Tecnici</th>
            <th className="p-2">Data</th>
            <th className="p-2">Stato</th>
            <th className="p-2">PDF</th>
            <th className="p-2">Allegati</th>
            <th className="p-2">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {righe.map(r => (
            <Fragment key={r.id}>
              <tr className="border-t">
                <td className="p-2">{r.oggetto}</td>
                <td className="p-2">{r.tecnici_nomi.join(', ') || '—'}</td>
                <td className="p-2">{r.data_apertura?.slice(0, 10) || '—'}</td>
                <td className="p-2"><span className="badge">{r.stato}</span></td>
                <td className="p-2">
                  {r.pdf_url && r.guerrato_id
                    ? <button
                        type="button"
                        className="btn-ghost"
                        onClick={async () => {
                          try {
                            const res = await rfiApi.downloadPdfGuerrato(r.guerrato_id as number)
                            const url = URL.createObjectURL(new Blob([res.data]))
                            const link = document.createElement('a')
                            link.href = url
                            link.download = `rapportino_${r.guerrato_id}.pdf`
                            document.body.appendChild(link)
                            link.click()
                            link.remove()
                            URL.revokeObjectURL(url)
                          } catch { toast.error('Download fallito') }
                        }}
                      >
                        <Download size={16} />
                      </button>
                    : '—'}
                </td>
                <td className="p-2">
                  <button className="btn-ghost" onClick={() => setEspansoOdl(espansoOdl === r.odl_id ? null : r.odl_id)}>
                    <Paperclip size={14} />
                  </button>
                </td>
                <td className="p-2">
                  <button className="btn-secondary text-xs" disabled={!r.pdf_url} onClick={() => setInviaId(r.id)}>
                    <Send size={12} className="inline mr-1" /> Invia
                  </button>
                </td>
              </tr>
              {espansoOdl === r.odl_id && <AllegatiRow ordineId={r.odl_id} />}
            </Fragment>
          ))}
        </tbody>
      </table>
      {inviaId !== null && <InviaModal rapportinoId={inviaId} onClose={() => setInviaId(null)} />}
    </div>
  )
}
